import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { message } from 'antd';
import getActivationFeePrerequisites, {
  ActivationFeePrerequisites,
} from 'api/SupraController/getActivationFeePrerequisites';
import type { BoardActiveContractWithRate } from '@bb/domain/dist/types';
import { AuthContext } from 'contexts';
import { featureEnabled } from 'utils';

type ActivationFeeContextType = {
  activationRate: string;
  canWaiveActivationFee: boolean;
  hasValidFullContract: boolean;
  refreshActivationFeeData: () => Promise<void>;
};

const ActivationFeeContext = createContext<ActivationFeeContextType | null>(
  null,
);

export const ActivationFeeContextProvider: React.FC = ({ children }) => {
  const { auth, hasFeature } = useContext(AuthContext);
  const [canWaiveActivationFee, setCanWaiveActivationFee] =
    useState<boolean>(false);
  const [activeContracts, setActiveContracts] = useState<
    BoardActiveContractWithRate[]
  >([]);

  const activationRate = useMemo(() => {
    const activationContract = activeContracts.find(
      c => c.contractRateLabel === 'ACC-ACTIVATION',
    );
    return activationContract?.rate || '';
  }, [activeContracts]);

  const hasValidFullContract = useMemo(() => activeContracts.some(
      contract =>
        typeof contract.contractRateLabel === 'string' &&
        contract.contractRateLabel.startsWith('ACC-F-'),
    ), [activeContracts]);

  const refreshActivationFeeData = useCallback(async () => {
    if (
      !featureEnabled('supraBillingApiFeature') ||
      !featureEnabled('keyholderBillingFeature') ||
      // The SUPRA_MVP flag is for Brokerages only at this time and needs to be on.
      // mlsStaffOrgs do not need the flag.
      (auth.org.type === 'brokerage' && !hasFeature('SUPRA_MVP')) ||
      !['brokerage', 'mlsStaff'].includes(auth.org.type)
    ) {
      return;
    }

    const result: ActivationFeePrerequisites =
      await getActivationFeePrerequisites({
        orgId: auth.org.id,
        orgType: auth.org.type as 'brokerage' | 'mlsStaff',
      });

    if (!result.activeContracts || result.activeContracts.length === 0) {
      message.warn('No Contracts found. Please contact Supra.');
      setActiveContracts([]);
    } else {
      setActiveContracts(
        result.activeContracts as BoardActiveContractWithRate[],
      );

      if (
        !result.activeContracts.some(contract =>
          contract.contractRateLabel?.startsWith('ACC-F-'),
        )
      ) {
        message.warn('No FULL Contracts found. Please contact Supra.');
      }
    }

    setCanWaiveActivationFee(Boolean(result.canWaiveActivationFee));
  }, [auth.org.id, auth.org.type, hasFeature]);
  useEffect(() => {
    refreshActivationFeeData();
  }, [refreshActivationFeeData]);

  return (
    <ActivationFeeContext.Provider
      value={{
        activationRate,
        canWaiveActivationFee,
        hasValidFullContract,
        refreshActivationFeeData,
      }}
    >
      {children}
    </ActivationFeeContext.Provider>
  );
};

export function useActivationFeeContext() {
  const ctx = useContext(ActivationFeeContext);
  if (!ctx) {
    throw new Error(
      'useActivationFeeContext must be used within an ActivationFeeContextProvider',
    );
  }
  return ctx;
}
